import { useRef, useState } from 'react'

const DEFAULT_LIMIT = 100

function resolveNext(next, prev) {
    return typeof next === 'function' ? next(prev) : next
}

function useHistoryState(initialState, opts = {}) {
    const { limit = DEFAULT_LIMIT, groupWithinMs = 0 } = opts
    const [history, setHistory] = useState({
        past: [],
        present: initialState,
        future: [],
    })
    const lastChangeRef = useRef(0)

    function setState(next) {
        const now = Date.now()
        const isGrouped =
            groupWithinMs > 0 && now - lastChangeRef.current < groupWithinMs
        lastChangeRef.current = now

        setHistory(function (h) {
            const present = resolveNext(next, h.present)
            if (present === h.present) {
                return h
            }
            if (isGrouped && h.past.length) {
                return { ...h, present, future: [] }
            }
            return {
                past: [...h.past, h.present].slice(-limit),
                present,
                future: [],
            }
        })
    }

    function undo() {
        lastChangeRef.current = 0
        setHistory(function (h) {
            if (!h.past.length) {
                return h
            }
            return {
                past: h.past.slice(0, -1),
                present: h.past[h.past.length - 1],
                future: [h.present, ...h.future],
            }
        })
    }

    function redo() {
        lastChangeRef.current = 0
        setHistory(function (h) {
            if (!h.future.length) {
                return h
            }
            return {
                past: [...h.past, h.present],
                present: h.future[0],
                future: h.future.slice(1),
            }
        })
    }

    function reset(...args) {
        lastChangeRef.current = 0
        setHistory(function (h) {
            return {
                past: [],
                present: args.length
                    ? resolveNext(args[0], h.present)
                    : h.present,
                future: [],
            }
        })
    }

    return [
        history.present,
        setState,
        {
            undo,
            redo,
            reset,
            canUndo: history.past.length > 0,
            canRedo: history.future.length > 0,
        },
    ]
}

export default useHistoryState
